// 무슨 일이 일어나는지 보여주는 버튼 — 관리자 전역 공통.
//
// 왜 만들었나: 버튼마다 busy/성공/실패 처리를 손으로 붙이다 보니 어떤 건 빠졌다.
// 실제로 스냅샷 삭제 버튼은 실패해도 **아무 표시가 없어** 운영자에겐 "고장난 버튼"으로만 보였다
// (원인은 CORS 였지만, 콘솔을 안 보는 사람에겐 알 길이 없다).
// → 버튼이 스스로 상태를 보여주게 만들어 그 부류의 실수를 구조적으로 막는다.
import { useEffect, useRef, useState } from "react";

type State = "idle" | "busy" | "done" | "error";

/**
 * contentApi 의 일부 함수는 상태코드를 그대로 던진다(`throw new Error("401")`) — 여러 호출부가
 * 그 문자열을 검사하고 있어 함부로 못 바꾼다. 표시 직전에 사람 말로 옮긴다.
 * 화면에 "401"만 뜨면 운영자는 뭘 해야 할지 모른다.
 */
const HUMAN_ERROR: Record<string, string> = {
  "401": "토큰이 만료됐어요. 새로고침 후 다시 로그인해 주세요.",
  "403": "권한이 없어요.",
  "503": "서버가 준비되지 않았어요.",
};
/** fetch 자체가 실패하면(CORS·엣지 다운) 브라우저는 뭉뚱그린 메시지만 준다 — 짚어준다. */
const NETWORK_HINT = "서버에 연결하지 못했어요. 엣지 서버가 떠 있는지 확인해 주세요.";

export function ActionButton({
  children,
  onAction,
  busyLabel,
  doneLabel = "완료",
  className = "btn",
  disabled,
  confirm,
  title,
}: {
  children: React.ReactNode;
  /** 실패하면 throw — 메시지가 버튼 옆에 뜬다. 삼키지 말 것. */
  onAction: () => Promise<unknown>;
  busyLabel?: string; // 기본: "<라벨> 중…" 대신 스피너만
  doneLabel?: string;
  className?: string;
  disabled?: boolean;
  confirm?: string; // 되돌리기 어려운 동작이면 확인 문구
  title?: string;
}) {
  const [state, setState] = useState<State>("idle");
  const [err, setErr] = useState<string | null>(null);
  const alive = useRef(true);
  // 언마운트 후 setState 방지. effect 시작에서 true 로 리셋 — StrictMode(dev)가 mount 시
  // effect 를 두 번 실행하며 cleanup 으로 false 를 만들어, 안 그러면 마운트 직후부터 계속 false 가 돼
  // async 완료 후 done 으로 못 넘어가고 '잠시만요' 로딩에 영영 갇힌다.
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  async function run() {
    if (confirm && !window.confirm(confirm)) return;
    setState("busy");
    setErr(null);
    try {
      await onAction();
      if (!alive.current) return;
      setState("done");
      // 성공 표시는 잠깐만 — 계속 남으면 다음 클릭 때 이전 결과인지 헷갈린다.
      window.setTimeout(() => alive.current && setState("idle"), 1800);
    } catch (e) {
      if (!alive.current) return;
      setState("error");
      // 실패 이유를 사람 말로 보여준다. 조용히 삼키면 "고장난 버튼"이 된다.
      const raw = (e as Error).message ?? "";
      setErr(HUMAN_ERROR[raw] ?? (/fetch|network|Failed to/i.test(raw) ? NETWORK_HINT : raw || "실패했어요"));
    }
  }

  return (
    <span className="act">
      <button
        type="button"
        className={className}
        onClick={() => void run()}
        disabled={disabled || state === "busy"}
        title={title}
        data-state={state}
      >
        {state === "busy" ? (
          <>
            <span className="act__spin" aria-hidden="true" />
            {busyLabel ?? "잠시만요…"}
          </>
        ) : state === "done" ? (
          <>✓ {doneLabel}</>
        ) : (
          children
        )}
      </button>
      {/* 실패는 버튼 옆에 붙여 어느 버튼이 실패했는지 헷갈리지 않게 */}
      {state === "error" && err ? (
        <span className="act__err" role="alert">
          {err}
        </span>
      ) : null}
    </span>
  );
}
